import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import translate from "../../../i18n/translate";
import { intl } from "../../../i18n/provider";
import ApiRequest from "../../../actions/ApiRequest";
import Toast from "../../common/Toast";

// Everything Toppinex carries for us with the one key. Shown so an admin
// knows what stops when the key does.
const CARRIED = [
  "Jawwal topups, bundles and invoices",
  "Ooredoo topups, bundles and invoices",
  "Hot Mobile topups and rollbacks",
  "Nedco and Selco electricity",
  "Mada, Paltel and Bnet bills",
];

const ToppinexSettings = ({ isDarktheme }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [stored, setStored] = useState({ base_url: "", configured: false, masked_key: "" });
  const [form, setForm] = useState({ base_url: "", api_key: "" });
  const [showKey, setShowKey] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const applyStored = (data) => {
    const next = {
      base_url: data.base_url || "",
      configured: Boolean(data.configured),
      masked_key: data.masked_key || "",
    };
    setStored(next);
    setForm({ base_url: next.base_url, api_key: "" });
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await ApiRequest.post("toppinex_credentials?operation=get");
      if (res?.data?.status === "success") applyStored(res.data);
    } catch (err) {
      // the axios interceptor already reports the failure
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const dirty = form.base_url.trim() !== stored.base_url || form.api_key.trim() !== "";

  const save = async (e) => {
    e.preventDefault();
    if (!dirty) return;
    const body = {};
    if (form.base_url.trim() !== stored.base_url) body.base_url = form.base_url.trim();
    if (form.api_key.trim()) body.api_key = form.api_key.trim();

    setSaving(true);
    setTestResult(null);
    try {
      const res = await ApiRequest.post("toppinex_credentials?operation=update", body);
      if (res?.data?.status === "success") {
        applyStored(res.data);
        setShowKey(false);
        Toast.fire({ title: translate("toppinexSaved"), icon: "success" });
      } else {
        Toast.fire({ title: res?.data?.reason || "Something Went Wrong!", icon: "error" });
      }
    } catch (err) {
      Toast.fire({ title: "Something Went Wrong!", icon: "error" });
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await ApiRequest.post("toppinex_credentials?operation=test", {}, { timeout: 60000 });
      setTestResult({ ok: res?.data?.status === "success", reason: res?.data?.reason });
    } catch (err) {
      setTestResult({ ok: false, reason: "Something Went Wrong!" });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Box sx={{ padding: 3, color: isDarktheme ? "#fff" : "inherit" }}>
      <Typography variant="body2" sx={{ marginBottom: 2, opacity: 0.75, maxWidth: "62ch" }}>
        {translate("toppinexHint")}
      </Typography>
      <Box component="ul" sx={{ marginTop: 0, marginBottom: 3, paddingLeft: 3, opacity: 0.85 }}>
        {CARRIED.map((line) => (
          <li key={line}>
            <Typography variant="body2">{line}</Typography>
          </li>
        ))}
      </Box>

      <Paper sx={{ padding: 3, maxWidth: 620, marginBottom: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, marginBottom: 2 }}>
          <Typography variant="subtitle1">{translate("API key")}</Typography>
          {loading ? (
            <CircularProgress size={18} />
          ) : stored.configured ? (
            <Chip size="small" color="success" label={stored.masked_key} />
          ) : (
            <Chip size="small" color="warning" label={translate("not set")} />
          )}
        </Box>

        <Box component="form" onSubmit={save} sx={{ display: "grid", gap: 2 }}>
          <TextField
            size="small"
            fullWidth
            label={translate("Base URL")}
            value={form.base_url}
            onChange={(e) => setForm((f) => ({ ...f, base_url: e.target.value }))}
            placeholder="https://api.toppinex.cc"
            autoComplete="off"
          />
          <TextField
            size="small"
            fullWidth
            label={translate("New API key")}
            type={showKey ? "text" : "password"}
            value={form.api_key}
            onChange={(e) => setForm((f) => ({ ...f, api_key: e.target.value }))}
            placeholder={stored.configured ? "pk_live_…  (" + intl.formatMessage({ id: "leave blank to keep" }) + ")" : "pk_live_…"}
            autoComplete="off"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowKey((v) => !v)} edge="end" aria-label="toggle key visibility">
                    {showKey ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button type="submit" variant="contained" disabled={saving || loading || !dirty}>
              {saving ? <CircularProgress size={20} color="inherit" /> : translate("Save")}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ padding: 3, maxWidth: 620 }}>
        <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
          {translate("Test key")}
        </Typography>
        <Typography variant="body2" sx={{ marginBottom: 2, opacity: 0.75 }}>
          {translate("toppinexTestHint")}
        </Typography>
        <Button variant="outlined" onClick={test} disabled={testing || loading || !stored.configured}>
          {testing ? <CircularProgress size={20} color="inherit" /> : translate("Test key")}
        </Button>
        {testResult && (
          <Alert severity={testResult.ok ? "success" : "error"} sx={{ marginTop: 2 }}>
            {testResult.reason}
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

const mapStateToProps = ({ darkTheme: { isDarktheme } }) => ({ isDarktheme });

export default connect(mapStateToProps)(ToppinexSettings);
